/**
 * Shared Skeleton components for loading states.
 * Amber-themed pulse animations matching OPP design system.
 */

interface SkeletonProps {
  className?: string;
}

/** Basic skeleton block */
export function Skeleton({ className = '' }: SkeletonProps) {
  return <div className={`animate-pulse rounded-lg bg-[hsl(var(--opp-accent-soft))] ${className}`} />;
}

/** Skeleton for a letter card in list view */
export function LetterCardSkeleton() {
  return (
    <div className="letter-card animate-pulse p-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-[hsl(var(--opp-accent-soft))] shrink-0" />
        <div className="flex-1 space-y-2.5">
          <div className="h-4 w-3/4 rounded bg-[hsl(var(--opp-accent-soft))]" />
          <div className="h-3 w-full rounded bg-[hsl(var(--opp-surface-raised))]" />
          <div className="h-3 w-2/3 rounded bg-[hsl(var(--opp-surface-raised))]" />
          <div className="flex gap-3 pt-1">
            <div className="h-3 w-16 rounded bg-[hsl(var(--opp-accent-soft))]" />
            <div className="h-3 w-20 rounded bg-[hsl(var(--opp-accent-soft))]" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Skeleton for museum/plaza grid items */
export function GridItemSkeleton() {
  return (
    <div className="letter-card animate-pulse overflow-hidden rounded-xl">
      <div className="h-32 bg-[hsl(var(--opp-accent-soft))]" />
      <div className="p-3 space-y-2">
        <div className="h-3.5 w-3/4 rounded bg-[hsl(var(--opp-accent-soft))]" />
        <div className="h-3 w-1/2 rounded bg-[hsl(var(--opp-surface-raised))]" />
        <div className="flex gap-2 pt-1">
          <div className="h-3 w-10 rounded bg-[hsl(var(--opp-accent-soft))]" />
          <div className="h-3 w-10 rounded bg-[hsl(var(--opp-accent-soft))]" />
        </div>
      </div>
    </div>
  );
}

/** Skeleton for profile page */
export function ProfileSkeleton() {
  return (
    <div className="min-h-screen bg-letter-paper">
      <div className="w-full h-48 md:h-56 bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
      <div className="mx-auto max-w-[var(--litopia-layout-1400px)] px-4 md:px-8 lg:px-12">
        <div className="flex items-end gap-5 -mt-16 md:-mt-20">
          <div className="h-28 w-28 md:h-36 md:w-36 rounded-full bg-[hsl(var(--opp-accent-soft))] border-4 border-on-image animate-pulse" />
          <div className="flex-1 pb-2 space-y-3">
            <div className="h-7 w-48 rounded bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
            <div className="h-4 w-32 rounded bg-[hsl(var(--opp-surface-raised))] animate-pulse" />
            <div className="h-4 w-40 rounded bg-[hsl(var(--opp-surface-raised))] animate-pulse" />
          </div>
        </div>
        <div className="mt-8 flex gap-8 justify-end">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="text-center space-y-1">
              <div className="h-6 w-8 mx-auto rounded bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
              <div className="h-3 w-12 rounded bg-[hsl(var(--opp-surface-raised))] animate-pulse" />
            </div>
          ))}
        </div>
        <div className="mt-4 border-b border-[hsl(var(--opp-border))] pb-3 flex gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-5 w-16 rounded bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Skeleton for settings page */
export function SettingsSkeleton() {
  return (
    <div className="space-y-4 pb-8">
      <div className="h-8 w-24 rounded bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
      <div className="h-10 w-full rounded-full bg-[hsl(var(--opp-surface-raised))] animate-pulse" />
      <div className="letter-card p-4 flex items-center gap-4">
        <div className="h-12 w-12 rounded-xl bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
        <div className="space-y-2 flex-1">
          <div className="h-4 w-32 rounded bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
          <div className="h-3 w-48 rounded bg-[hsl(var(--opp-surface-raised))] animate-pulse" />
        </div>
      </div>
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="letter-card p-4 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
          <div className="space-y-2 flex-1">
            <div className="h-4 w-40 rounded bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
            <div className="h-3 w-56 rounded bg-[hsl(var(--opp-surface-raised))] animate-pulse" />
          </div>
          <div className="h-4 w-4 rounded bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
        </div>
      ))}
    </div>
  );
}

/** Skeleton for write page */
export function WriteSkeleton() {
  return (
    <div className="space-y-5 pb-8">
      <div className="space-y-2">
        <div className="h-4 w-20 rounded bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 rounded-xl bg-[hsl(var(--opp-surface-raised))] border-2 border-[hsl(var(--opp-border))] animate-pulse" />
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-4 w-20 rounded bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-24 rounded-xl bg-[hsl(var(--opp-surface-raised))] border-2 border-[hsl(var(--opp-border))] animate-pulse" />
          ))}
        </div>
      </div>
      <div className="h-12 w-full rounded-xl bg-[hsl(var(--opp-surface-raised))] animate-pulse" />
      <div className="h-[var(--litopia-layout-400px)] w-full rounded-2xl bg-[hsl(var(--opp-surface-raised))] border border-[hsl(var(--opp-border))] animate-pulse" />
    </div>
  );
}

/** Generic page skeleton with header placeholder */
export function PageSkeleton() {
  return (
    <div className="space-y-4 pb-8">
      <div className="h-7 w-40 rounded bg-[hsl(var(--opp-accent-soft))] animate-pulse" />
      <div className="h-4 w-64 rounded bg-[hsl(var(--opp-surface-raised))] animate-pulse" />
      <div className="space-y-3 mt-6">
        <LetterCardSkeleton />
        <LetterCardSkeleton />
        <LetterCardSkeleton />
      </div>
    </div>
  );
}
